import React, { useContext, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { useLocation } from "./location";

export const LOGIN_REDIRECT_PARAM = "login_redirect";
export const ACCESS_TOKEN_COOKIE_NAME = "aqora-access-token";

export interface TokenClaims {
  sub: string;
  sss: string;
  typ: "refresh" | "access";
  iat: number;
  exp: number;
}

export const jwtClaims = (token: string): TokenClaims => jwtDecode(token);

export interface AuthContextValue {
  getToken: () => string | undefined;
  setTokens: (tokens: { access?: string; refresh?: string }) => void;
  revokeToken: (token_type: "access" | "refresh") => void;
  revokeTokens: () => void;
  isAuthenticated: boolean;
  userId?: string | undefined;
}

export const AuthContext = React.createContext<AuthContextValue>({
  getToken: () => undefined,
  setTokens: () => {},
  revokeToken: () => {},
  revokeTokens: () => {},
  isAuthenticated: false,
});

export const useAuth = () => useContext(AuthContext);

export const useLoginRedirect = () => {
  const { pathname } = useLocation();
  return `/login?${LOGIN_REDIRECT_PARAM}=${encodeURIComponent(pathname)}`;
};

export const useOnAuthCallback = () => {
  const location = useLocation();
  const [searchParams, _setSearchParams] = useSearchParams();
  return useCallback(() => {
    const addNoIndexMetaTag = () => {
      if (!document.querySelector('meta[name="robots"]')) {
        const metaTag = document.createElement("meta");
        metaTag.name = "robots";
        metaTag.content = "noindex, nofollow";
        document.head.appendChild(metaTag);
      }
    };
    if (searchParams.has(LOGIN_REDIRECT_PARAM)) {
      const redirect = searchParams.get(LOGIN_REDIRECT_PARAM)!;
      addNoIndexMetaTag();
      return location.replace(redirect);
    }
    if (searchParams.has("client_id")) {
      addNoIndexMetaTag();
      return location.replace(`/oauth2/authorize?${searchParams.toString()}`);
    }
    location.replace("/");
  }, [searchParams, location]);
};
